import { useRef } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { X, Plus } from "lucide-react";

export function MultiProductPicker({ items, setItems, max = 4 }) {
  const fileRef = useRef();
  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []).filter(
      (f) => f.type.startsWith("image/") || f.type.startsWith("video/")
    );
    const next = [...items];
    for (const f of incoming) {
      if (next.length >= max) break;
      next.push({ file: f, preview: URL.createObjectURL(f), desc: "", isVideo: f.type.startsWith("video/") });
    }
    setItems(next);
  };
  const update = (i, patch) => setItems(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const removeAt = (i) => setItems(items.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      <Label>Photos or videos ({items.length}/{max})</Label>
      <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="mp-file-input" />
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2 rounded-lg border border-border p-2" data-testid={`mp-item-${i}`}>
          {it.isVideo ? (
            <video src={it.preview} className="w-14 h-14 rounded object-cover flex-shrink-0 bg-black" muted playsInline preload="metadata" />
          ) : (
            <img src={it.preview} alt="" className="w-14 h-14 rounded object-cover flex-shrink-0 bg-black" />
          )}
          <Input value={it.desc} onChange={(e) => update(i, { desc: e.target.value })}
            placeholder={it.isVideo ? `Video ${i + 1} — describe it (we use a frame)` : `Photo ${i + 1} — describe it (e.g. "granite countertop")`}
            className="flex-1 h-9 text-sm" data-testid={`mp-desc-${i}`} />
          <button type="button" onClick={() => removeAt(i)} data-testid={`mp-remove-${i}`}
            className="text-muted-foreground hover:text-destructive p-1"><X className="w-4 h-4" /></button>
        </div>
      ))}
      {items.length < max && (
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="mp-add-btn"
          className="w-full rounded-lg border-2 border-dashed border-border hover:border-primary/60 transition-colors p-3 flex items-center justify-center gap-2 text-muted-foreground text-sm">
          <Plus className="w-4 h-4" /> {items.length === 0 ? "Upload photo(s) or video(s)" : "Add another photo / video"}
        </button>
      )}
      <p className="text-[11px] text-muted-foreground">Videos are supported — we pull a clean frame to design around.</p>
    </div>
  );
}
